import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LoginService } from '@shared-angular/services/login.service';
import { RouteShareService } from './route-share.service';

export interface SharePointItem {
  id: string;
  name: string;
  folder?: unknown;
  webUrl?: string;
  [key: string]: unknown;
}

export interface SharePointPage {
  id: string;
  title: string;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class SharePointService {
  constructor(private http: HttpClient, private rss: RouteShareService, private loginService: LoginService) { }


  public get isSharepointAvailable() {
    return this.loginService.myOrgs.find((org) =>
      org.short == this.rss.projectService.selectedProject?.orgShort &&
      org.sharepointClientId.length > 8

    ) != null;
  }

  public async readFolder(folderId: string) {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.get<SharePointItem[]>(
          `/api/admin/project/sharepoint/read/${this.rss.projectService.selectedProject._id}/drive/${folderId}`
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }

  public async selectFile(file: SharePointItem) {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.post(
          `/api/admin/project/sharepoint/write/${this.rss.projectService.selectedProject._id}/selectfile`,
          file
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }

  public async replaceFile(file: SharePointItem, docId: string) {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.post(
          `/api/admin/project/sharepoint/write/${this.rss.projectService.selectedProject._id}/replacefile`,
          { ...file, docId }
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }

  public async readPages() {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.get<SharePointPage[]>(
          `/api/admin/project/sharepoint/read/${this.rss.projectService.selectedProject._id}/pages`
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }

  public async selectPage(page: SharePointPage) {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.post(
          `/api/admin/project/sharepoint/write/${this.rss.projectService.selectedProject._id}/selectpage`,
          page
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }

  public async replacePage(page: SharePointPage, docId: string) {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.post(
          `/api/admin/project/sharepoint/write/${this.rss.projectService.selectedProject._id}/replacepage`,
          { ...page, docId }
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }

  public async listSubscriptions() {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.get(
          `/api/admin/project/sharepoint/write/${this.rss.projectService.selectedProject._id}/subscriptions`
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }

  public async createSubscription(notificationUrl: string, clientState?: string, expirationDateTime?: string) {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.post(
          `/api/admin/project/sharepoint/write/${this.rss.projectService.selectedProject._id}/subscriptions`,
          { notificationUrl, clientState, expirationDateTime }
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }

  public async deleteSubscription(subscriptionId: string) {
    if (this.rss.projectService.selectedProject) {
      return await firstValueFrom(
        this.http.delete(
          `/api/admin/project/sharepoint/write/${this.rss.projectService.selectedProject._id}/subscriptions/${subscriptionId}`
        )
      );
    } else {
      throw new Error('No project selected');
    }
  }
}
